
/**
 * Module dependencies.
 */

import { CopyToClipboard as CopyButton } from 'react-copy-to-clipboard';
import { IconButton, IconButtonProps, Size } from './buttons/icon-button';
import { Text } from './typography';
import { color } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { useCallback, useEffect, useState } from 'react';
import copyIcon from './assets/svg/16/copy.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = Omit<IconButtonProps, 'icon' | 'iconSize' | 'size'> & {
  className?: string;
  copiedLabel: string;
  icon?: IconButtonProps['icon'];
  iconSize?: Size | string;
  size?: Size | string;
  value: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  position: relative;
`;

/**
 * `Notification` styled component.
 */

const Notification = styled(Text).attrs({ as: 'span', size: 'small' })<{ isVisible: boolean }>`
  animation: 0.5s linear forwards;
  background-color: ${color('gray400')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  bottom: -32px;
  color: ${color('white')};
  left: 50%;
  opacity: 0;
  padding: 4px 8px;
  position: absolute;
  transform: translateX(-50%);

  ${ifProp('isVisible', css`
    animation-name: ${theme('keyframes.fadeIn')};
  `, css`
    animation-name: ${theme('keyframes.fadeOut')};
  `)}
`;

/**
 * Export `CopyToClipboard` component.
 */

export function CopyToClipboard(props: Props): JSX.Element {
  const {
    colorTheme = 'transparent',
    copiedLabel,
    className,
    icon = copyIcon,
    iconSize = 'small',
    size = '16px',
    value,
    ...buttonProps
  } = props;

  const [copyCount, setCopyCount] = useState<number>(0);
  const [isVisible, setVisible] = useState<boolean>(false);
  const handleCopy = useCallback(() => {
    setCopyCount((count: number) => (count + 1) % 100);
    setVisible(true);
  }, []);

  useEffect(() => {
    if (copyCount <= 0) {
      return;
    }

    const timeout = setTimeout(() => {
      setVisible(false);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [copyCount]);

  useEffect(() => {
    if (!isVisible) {
      const timeout = setTimeout(() => {
        setCopyCount(0);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isVisible]);

  return (
    <Wrapper className={className}>
      <CopyButton
        onCopy={handleCopy}
        text={value}
      >
        <IconButton
          {...buttonProps}
          colorTheme={colorTheme}
          icon={icon}
          iconSize={iconSize}
          size={size}
        />
      </CopyButton>

      {copyCount > 0 && (
        <Notification
          aria-hidden={!isVisible}
          isVisible={isVisible}
        >
          {copiedLabel}
        </Notification>
      )}
    </Wrapper>
  );
}
